import {
    booleanAttribute,
    Component,
    computed,
    HostBinding,
    inject,
    Input,
    input,
    numberAttribute,
    signal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { KONE, PRIMENG, TIcon, TIconType } from '@app/components/_ui/icon/icon';
import { TColors } from '@app/interfaces/root-type.interface';
import { AppStyles } from '@app/services/app-styles.service';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
    selector: 'icon',
    template: `
        @if (!loading()) {
            @switch (type()) {
                @case ('google') {
                    <i
                        class="material-icons-outlined"
                        [style]="{
                            color: colorFormatted(),
                            fontSize: fontSize(),
                        }">
                        {{ _name() }}
                    </i>
                }
                @case ('kone') {
                    <i
                        class="KONE"
                        [style]="{
                            '--color': colorFormatted(),
                            '--rotation': rotate() + 'deg',
                            '--path': svgPath(),
                        }"></i>
                }
                @case ('primeng') {
                    <i
                        [class]="'pi ' + _name()"
                        [style]="{
                            color: colorFormatted(),
                            fontSize: fontSize(),
                            transform: 'rotate(' + rotate() + 'deg)',
                        }"></i>
                }
            }
        } @else {
            <p-skeleton shape="circle" [size]="fontSize()" />
        }
    `,
    styles: `
        :host {
            display: flex;
            align-items: center;
            place-content: center;

            &.disable {
                span,
                i {
                    pointer-events: none;
                    opacity: 0.4;
                }
            }
        }
        i {
            transition: all var(--transition-duration-200);
        }

        i.KONE {
            background-color: var(--color);
            mask: var(--path) no-repeat center;
            mask-size: contain;
            -webkit-mask: var(--path) no-repeat center;
            -webkit-mask-size: contain;
            display: block;
            width: var(--size);
            height: var(--size);
            transform: rotate(var(--rotation));
            transition: all var(--transition-duration-200);
        }
    `,
    imports: [SkeletonModule],
})
export class IconComponent {
    private appStyles: AppStyles = inject(AppStyles);

    @Input({ required: true })
    set name(name: TIcon) {
        this.setIcon(name);
    }

    color = input<TColors>();
    customColor = input<string>(''); // overwrites color
    rotateDeg = input<number, unknown>(0, { transform: numberAttribute });
    size = input<number, unknown>(0, { transform: numberAttribute });
    rotate = input<number, unknown>(0, { transform: numberAttribute });
    disabled = input<boolean, unknown>(false, { transform: booleanAttribute });
    loading = input<boolean, unknown>(false, { transform: booleanAttribute });

    type: Signal<TIconType> = computed(() => this.getType());
    colorFormatted: Signal<string> = computed(() => this.getColorFormatted());
    fontSize: Signal<string> = computed(() => this.getFontSize());
    svgPath: Signal<string> = computed(() => `url('assets/icons/svg/${this._name()}.svg')`);

    _name: WritableSignal<TIcon> = signal('' as TIcon);

    @HostBinding('class')
    get class() {
        return 'icon';
    }

    @HostBinding('class.disable')
    get _disable() {
        return this.disabled();
    }

    @HostBinding('style.--size')
    get sizeVar() {
        return this.getFontSize();
    }

    private setIcon(name: TIcon) {
        this._name.set(name);
    }

    private getFontSize() {
        let size = this.size() ? this.size() : this.appStyles.getNumberFromPixels('iconSize');
        // KONE icon looks smaller than other icons - we decrease icon size to make it similar
        if (this.type() !== 'kone') size = size - 3;
        return size + 'px';
    }

    private getType(): TIconType {
        if (PRIMENG.includes(this._name() as any)) return 'primeng';
        else if (KONE.includes(this._name() as any)) return 'kone';
        else return 'google';
    }

    private getColorFormatted(): string {
        if (this.customColor()) return this.customColor();
        else if (this.color()) return this.appStyles.getColor(this.color()!);
        else return this.appStyles.getTextColor('text-color');
    }
}
